import {useEffect, useState, type ReactNode} from 'react';
import {type LucideIcon, ChevronRight} from 'lucide-react';
import {Link, useLocation} from 'react-router-dom';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@common-web/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';

// Recursive type for nested navigation items
export interface NavItem {
  title: string;
  url?: string;
  icon?: LucideIcon;
  items?: NavItem[];
  suffix?: ReactNode;
}

// The nav url the current page belongs to: the longest url that is the path itself or one of its
// parents, so detail pages (e.g. /groups/123) keep their section highlighted.
export function findActiveUrl(items: NavItem[], pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  let best: string | null = null;
  const visit = (list: NavItem[]) => {
    for (const item of list) {
      const url = item.url;
      const matches = url && (path === url || (url !== '/' && path.startsWith(url + '/')));
      if (matches && (!best || url.length > best.length)) best = url;
      if (item.items) visit(item.items);
    }
  };
  visit(items);
  return best;
}

const containsUrl = (item: NavItem, url: string | null): boolean =>
  url !== null && (item.url === url || (item.items ?? []).some((sub) => containsUrl(sub, url)));

// Opens itself when navigation lands inside it; otherwise keeps whatever the user chose.
function useFolderOpen(isActive: boolean) {
  const [open, setOpen] = useState(isActive);
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);
  return [open, setOpen] as const;
}

interface MenuItemProps {
  item: NavItem;
  depth: number;
  activeUrl: string | null;
}

// Defined at module level (not inside NavMain) so folders keep their open state across renders.
function RecursiveMenuItem({item, depth, activeUrl}: MenuItemProps) {
  const hasChildren = item.items && item.items.length > 0;
  const isActive = containsUrl(item, activeUrl);
  const isCurrentPathActive = Boolean(item.url) && item.url === activeUrl;
  const [open, setOpen] = useFolderOpen(isActive);

  // For items with children, render as collapsible
  if (hasChildren) {
    return (
      <Collapsible asChild open={open} onOpenChange={setOpen} className='group/collapsible'>
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={item.title}
              style={{paddingLeft: depth > 0 ? `${depth * 0.75 + 0.75}rem` : undefined}}
              className='relative overflow-hidden transition-all duration-200'
            >
              {item.icon && depth === 0 && (
                <item.icon
                  className={`
                    transition-all duration-200
                    ${isActive ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/60'}
                  `}
                />
              )}
              <span className='font-medium'>{item.title}</span>
              <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.items!.map((subItem) => (
                <RecursiveMenuSubItem
                  key={subItem.title}
                  item={subItem}
                  depth={depth + 1}
                  activeUrl={activeUrl}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  // For leaf items (no children), render as simple menu button
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={isCurrentPathActive}
        style={{paddingLeft: depth > 0 ? `${depth * 0.75 + 0.75}rem` : undefined}}
        className='relative overflow-hidden transition-all duration-200'
      >
        <Link to={item.url!}>
          {item.icon && depth === 0 && (
            <item.icon
              className={`
                transition-all duration-200
                ${isCurrentPathActive ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/60'}
              `}
            />
          )}
          <span className='font-medium'>{item.title}</span>
          {item.suffix && <span className='ml-auto'>{item.suffix}</span>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// Recursive component for submenu items
function RecursiveMenuSubItem({item, depth, activeUrl}: MenuItemProps) {
  const hasChildren = item.items && item.items.length > 0;
  const isActive = containsUrl(item, activeUrl);
  const isCurrentPathActive = Boolean(item.url) && item.url === activeUrl;
  const [open, setOpen] = useFolderOpen(isActive);

  if (hasChildren) {
    return (
      <Collapsible
        asChild
        open={open}
        onOpenChange={setOpen}
        className='group/collapsible cursor-pointer'
      >
        <SidebarMenuSubItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton
              style={{paddingLeft: `${depth * 0.75}rem`}}
              className='transition-all duration-200 w-full'
            >
              <div className='flex items-center gap-2 w-full'>
                <span className='font-medium'>{item.title}</span>
                <ChevronRight className='ml-auto h-3 w-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
              </div>
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.items!.map((subItem) => (
                <RecursiveMenuSubItem
                  key={subItem.title}
                  item={subItem}
                  depth={depth + 1}
                  activeUrl={activeUrl}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuSubItem>
      </Collapsible>
    );
  }

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        asChild
        isActive={isCurrentPathActive}
        style={{paddingLeft: `${depth * 0.75}rem`}}
        className='transition-all duration-200'
      >
        <Link to={item.url!}>
          <span className='font-medium'>{item.title}</span>
          {item.suffix && <span className='ml-auto'>{item.suffix}</span>}
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

export function NavMain({items}: {items: NavItem[]}) {
  const location = useLocation();
  const activeUrl = findActiveUrl(items, location.pathname);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <RecursiveMenuItem key={item.title} item={item} depth={0} activeUrl={activeUrl} />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
